/**
 * Socket.IO Event Types for Match Flow
 *
 * Defines the protocol for real-time communication during match flow execution.
 */

import type { FlowPlayer, MatchFlowState, PlayerAction } from "./types.js";

// =============================================================================
// SERVER → CLIENT EVENTS
// =============================================================================

/**
 * Emitted when a flow starts for a match.
 */
export type MatchFlowStartedEvent = {
  matchId: string;
  flowId: string;
  players: FlowPlayer[];
  startNodeId: string;
  startedAt: number;
};

/**
 * Emitted when a new node becomes active.
 */
export type MatchFlowNodeStartedEvent = {
  matchId: string;
  nodeId: string;
  nodeType: string;
  config: Record<string, unknown>;
  state: Record<string, unknown>;
  timeoutAt: number | null;
  startedAt: number;
};

/**
 * Emitted when a node's state changes (e.g., new vote, player ready).
 */
export type MatchFlowNodeUpdateEvent = {
  matchId: string;
  nodeId: string;
  state: Record<string, unknown>;
  updatedAt: number;
};

/**
 * Emitted when a node completes successfully.
 */
export type MatchFlowNodeCompletedEvent = {
  matchId: string;
  nodeId: string;
  result: Record<string, unknown>;
  completedAt: number;
};

/**
 * Emitted when a node times out.
 */
export type MatchFlowNodeTimeoutEvent = {
  matchId: string;
  nodeId: string;
  fallbackResult: Record<string, unknown>;
  timedOutAt: number;
};

/**
 * Emitted when a node encounters an error.
 */
export type MatchFlowNodeErrorEvent = {
  matchId: string;
  nodeId: string;
  error: string;
  errorAt: number;
};

/**
 * Emitted when the entire flow completes successfully.
 */
export type MatchFlowCompletedEvent = {
  matchId: string;
  context: Record<string, unknown>;
  completedAt: number;
};

/**
 * Emitted when the flow fails.
 */
export type MatchFlowFailedEvent = {
  matchId: string;
  error: string;
  failedNodeId: string | null;
  failedAt: number;
};

/**
 * Emitted when a player disconnects during the flow.
 */
export type MatchFlowPlayerDisconnectedEvent = {
  matchId: string;
  userId: string;
  disconnectedAt: number;
};

/**
 * Emitted when a player reconnects during the flow.
 */
export type MatchFlowPlayerReconnectedEvent = {
  matchId: string;
  userId: string;
  reconnectedAt: number;
};

/**
 * Full flow state sync (sent on join or reconnect).
 */
export type MatchFlowStateSyncEvent = {
  matchId: string;
  state: MatchFlowState;
};

// =============================================================================
// CLIENT → SERVER EVENTS
// =============================================================================

/**
 * Client requests to join a flow room and get current state.
 */
export type MatchFlowJoinPayload = {
  matchId: string;
};

/**
 * Client requests to leave a flow room.
 */
export type MatchFlowLeavePayload = {
  matchId: string;
};

/**
 * Client submits an action for the current node.
 */
export type MatchFlowActionPayload = {
  matchId: string;
  nodeId: string;
  action: PlayerAction;
};

/**
 * Response to an action submission.
 */
export type MatchFlowActionResponse = {
  success: boolean;
  error?: string;
  newState?: Record<string, unknown>;
};

// =============================================================================
// EVENT NAME CONSTANTS
// =============================================================================

export const MATCH_FLOW_EVENTS = {
  // Server → Client
  STARTED: "match_flow:started",
  NODE_STARTED: "match_flow:node_started",
  NODE_UPDATE: "match_flow:node_update",
  NODE_COMPLETED: "match_flow:node_completed",
  NODE_TIMEOUT: "match_flow:node_timeout",
  NODE_ERROR: "match_flow:node_error",
  COMPLETED: "match_flow:completed",
  FAILED: "match_flow:failed",
  PLAYER_DISCONNECTED: "match_flow:player_disconnected",
  PLAYER_RECONNECTED: "match_flow:player_reconnected",
  STATE_SYNC: "match_flow:state_sync",

  // Client → Server
  JOIN: "match_flow:join",
  LEAVE: "match_flow:leave",
  ACTION: "match_flow:action",
} as const;

// =============================================================================
// TYPE MAPS FOR SOCKET.IO
// =============================================================================

/**
 * Server-to-client event map.
 */
export type MatchFlowServerEvents = {
  [MATCH_FLOW_EVENTS.STARTED]: (event: MatchFlowStartedEvent) => void;
  [MATCH_FLOW_EVENTS.NODE_STARTED]: (event: MatchFlowNodeStartedEvent) => void;
  [MATCH_FLOW_EVENTS.NODE_UPDATE]: (event: MatchFlowNodeUpdateEvent) => void;
  [MATCH_FLOW_EVENTS.NODE_COMPLETED]: (event: MatchFlowNodeCompletedEvent) => void;
  [MATCH_FLOW_EVENTS.NODE_TIMEOUT]: (event: MatchFlowNodeTimeoutEvent) => void;
  [MATCH_FLOW_EVENTS.NODE_ERROR]: (event: MatchFlowNodeErrorEvent) => void;
  [MATCH_FLOW_EVENTS.COMPLETED]: (event: MatchFlowCompletedEvent) => void;
  [MATCH_FLOW_EVENTS.FAILED]: (event: MatchFlowFailedEvent) => void;
  [MATCH_FLOW_EVENTS.PLAYER_DISCONNECTED]: (event: MatchFlowPlayerDisconnectedEvent) => void;
  [MATCH_FLOW_EVENTS.PLAYER_RECONNECTED]: (event: MatchFlowPlayerReconnectedEvent) => void;
  [MATCH_FLOW_EVENTS.STATE_SYNC]: (event: MatchFlowStateSyncEvent) => void;
};

/**
 * Client-to-server event map.
 */
export type MatchFlowClientEvents = {
  [MATCH_FLOW_EVENTS.JOIN]: (
    payload: MatchFlowJoinPayload,
    callback: (state: MatchFlowState | null) => void,
  ) => void;
  [MATCH_FLOW_EVENTS.LEAVE]: (payload: MatchFlowLeavePayload) => void;
  [MATCH_FLOW_EVENTS.ACTION]: (
    payload: MatchFlowActionPayload,
    callback: (response: MatchFlowActionResponse) => void,
  ) => void;
};
